import React, { lazy, Suspense } from 'react';
import { Link, Route, Switch, useRouteMatch } from 'react-router-dom';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import CircularProgress from '~/components/core/Atomic/CircularProgress/CircularProgress';
import type { InnerTabSingleProps } from '~/components/core/Layout/InnerTabs/InnerTabs';
import InnerTabs from '~/components/core/Layout/InnerTabs/InnerTabs';
import type { EmailTemplateDocumentSelectorProps } from '~/components/GenericTemplates/GenericTemplatesConfiguration/EmailTemplateDocumentSelector/EmailTemplateDocumentSelector';
import EmailTemplateDocumentSelector from '~/components/GenericTemplates/GenericTemplatesConfiguration/EmailTemplateDocumentSelector/EmailTemplateDocumentSelector';
import { getGenericTemplateBuilderDefaultFormValues } from '~/components/GenericTemplates/GenericTemplatesConfiguration/GenericTemplateBuilder/GenericTemplateBuilderContainer';
import type {
  GenericTemplatesAdditionalPropsType,
  GenericTemplatesTableContainerPropsType,
} from '~/components/GenericTemplates/GenericTemplatesConfiguration/GenericTemplatesTableContainer';
import GenericTemplatesTableContainer from '~/components/GenericTemplates/GenericTemplatesConfiguration/GenericTemplatesTableContainer';
import MoiEmailTemplatesContainer from '~/components/GenericTemplates/GenericTemplatesConfiguration/MoiEmail/MoiEmailTemplatesContainer';
import TokensViewerPage from '~/components/GenericTemplates/GenericTemplatesConfiguration/TokensViewer/TokensViewerPage';
import type {
  EmailTemplateFormSubmitValuesProps,
  NotificationTemplateFormSubmitValuesProps,
  TemplateFormSubmitValuesProps,
} from '~/components/GenericTemplates/types';
import { useCms } from '~/components/hooks/useCms';
import { BracketsCurlyIcon } from '~/components/icons';
import type { NotificationConfigurationFieldsFormikInnerProps } from '~/components/SystemConfiguration/NotificationUtils/NotificationConfigurationFieldsFormikInner';
import NotificationConfigurationFieldsFormikInner from '~/components/SystemConfiguration/NotificationUtils/NotificationConfigurationFieldsFormikInner';
import OperationsBreadcrumbs from '~/components/SystemConfiguration/OperationsBreadcrumbs';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import colors from '~/theme/tailwind/colors';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isUserFromSuperOrganization } from '~/UserUtils';
import { isFeatureEnabled } from '~/Utils';

import { DOCUMENT_TEMPLATES_ROUTES } from '../utils/routes';

// Lazy, because this screen is mounted on the customer-facing Templates tab as well as the internal
// admin one, and Token Values is Five Sigma-only. A static import would put the page, its panel and
// table, and the claim search they pull in, into every organization's bundle for a route they can
// never reach. The chunk is fetched when the route actually renders - so not at all unless a Five
// Sigma user opens it.
const ClaimTokenValuesPage = lazy(
  () =>
    import(
      /* webpackChunkName: "claimTokenValues" */ '~/components/GenericTemplates/GenericTemplatesConfiguration/TokenValues/ClaimTokenValuesPage'
    )
);

const GenericTemplatesConfigurationScreen: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const { user } = useCms();
  const match = useRouteMatch();

  // Token Values is a Five Sigma support tool, so it is gated on two independent facts. The route
  // check is what keeps it off the customer-facing Templates screen, which renders this very same
  // component; the organization check matches the route's own `abort_unless_super_org`, which reads
  // the same organization this flag is derived from - so an impersonating user is refused by both.
  const isInternalAdminRoute = match.url.startsWith('/admin');
  const canInspectTokenValues = isInternalAdminRoute && isUserFromSuperOrganization(user);
  const isMoiEmailTemplatesEnabled = isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.MOI_EMAIL_TEMPLATES);

  const notificationFields: GenericTemplatesAdditionalPropsType<
    NotificationConfigurationFieldsFormikInnerProps,
    NotificationTemplateFormSubmitValuesProps
  > = {
    component: NotificationConfigurationFieldsFormikInner,
    props: { isDueDateImmediateField: 'is_due_date_immediate' },
    defaultFormValues: {
      ...getGenericTemplateBuilderDefaultFormValues('notification', organization),
      priority: undefined,
      category_id: undefined,
      is_due_date_immediate: undefined,
      due_date_number: undefined,
      due_date_timeframe: undefined,
    },
    validationSchemaExtension: {
      priority: Yup.string().nullable().required('Required'),
      is_due_date_immediate: Yup.boolean().nullable().required('Required'),
      due_date_number: Yup.number()
        .nullable()
        .when('is_due_date_immediate', {
          is: false,
          then: Yup.number().nullable().required('Required'),
          otherwise: Yup.number().nullable(),
        }),
      due_date_timeframe: Yup.string()
        .nullable()
        .when('is_due_date_immediate', {
          is: false,
          then: Yup.string().nullable().required('Required'),
          otherwise: Yup.string().nullable(),
        }),
    },
  };

  const emailDocumentSelectorProps: GenericTemplatesAdditionalPropsType<
    EmailTemplateDocumentSelectorProps,
    EmailTemplateFormSubmitValuesProps
  > = {
    component: EmailTemplateDocumentSelector,
    props: { documentTemplateFieldId: 'document_template_id' },
    defaultFormValues: {
      ...getGenericTemplateBuilderDefaultFormValues('email', organization),
      document_template_id: null,
    },
    validationSchemaExtension: {
      document_template_id: Yup.number().nullable(),
    },
  };

  type TabProps =
    | GenericTemplatesTableContainerPropsType<object, TemplateFormSubmitValuesProps>
    | GenericTemplatesTableContainerPropsType<EmailTemplateDocumentSelectorProps, EmailTemplateFormSubmitValuesProps>
    | GenericTemplatesTableContainerPropsType<
        NotificationConfigurationFieldsFormikInnerProps,
        NotificationTemplateFormSubmitValuesProps
      >;

  const tabs: InnerTabSingleProps<TabProps>[] = [
    {
      label: 'Documents',
      url: 'document_templates',
      component: GenericTemplatesTableContainer as React.FC<TabProps>,
      props: {
        templateType: 'document',
        isCreateTemplateEnabled: false, // TODO: Enable this in NGTPA-17180
        shouldDisplayType: false,
        templateRoutesDict: DOCUMENT_TEMPLATES_ROUTES,
      },
    },
    {
      label: 'Email',
      url: 'email_templates',
      component: GenericTemplatesTableContainer as React.FC<TabProps>,
      props: {
        templateType: 'email',
        isCreateTemplateEnabled: true,
        shouldDisplayType: false,
        additionalProps: emailDocumentSelectorProps,
      },
    },
    {
      label: 'Notes',
      url: 'notes_templates',
      component: GenericTemplatesTableContainer as React.FC<TabProps>,
      props: {
        templateType: 'note',
        isCreateTemplateEnabled: true,
        shouldDisplayType: false,
      },
    },
    {
      label: 'Notifications',
      url: 'notification_templates',
      component: GenericTemplatesTableContainer as React.FC<TabProps>,
      props: {
        templateType: 'notification',
        isCreateTemplateEnabled: true,
        shouldDisplayType: false,
        additionalProps: notificationFields,
      },
    },
    {
      label: 'SMS',
      url: 'sms_templates',
      component: GenericTemplatesTableContainer as React.FC<TabProps>,
      props: {
        templateType: 'sms',
        isCreateTemplateEnabled: true,
        shouldDisplayType: false,
      },
    },
    {
      // "MOI" tab is frontend-gated on MOI_EMAIL_TEMPLATES so only entitled orgs see it. It owns its
      // own dedicated /moi/email data surface, so it does not use GenericTemplatesTableContainer.
      label: 'MOI',
      url: 'moi_email_templates',
      component: MoiEmailTemplatesContainer as React.FC<TabProps>,
      isTabHidden: !isMoiEmailTemplatesEnabled,
    },
  ];

  const activeTabs = tabs.filter((tab) => !tab.isTabHidden);

  return (
    <Switch>
      <Route path={`${match.path}/tokens`} component={TokensViewerPage} />
      {/* The Suspense boundary goes inside `render`, not around the `Route`: `Switch` picks its
          match by reading each child's own `path` prop, so a wrapper element between the two would
          have no path and swallow every route. Local rather than leaning on the app-level boundary
          in `CmsMainAuthenticatedInner`, which would blank the whole screen while the chunk loads. */}
      {canInspectTokenValues && (
        <Route
          path={`${match.path}/token_values`}
          render={() => (
            <Suspense fallback={<CircularProgress />}>
              <ClaimTokenValuesPage />
            </Suspense>
          )}
        />
      )}
      <Route path={match.path}>
        <div className="m-20">
          <div className="mb-12 flex items-center justify-between">
            <OperationsBreadcrumbs currentTab="Templates" />
            <div className="flex items-center gap-8">
              <Button color="primary" component={Link} to={`${match.url}/tokens`}>
                <BracketsCurlyIcon iconColor={colors.teal[700]} size={20} />
                <span className="ml-8">View Tokens</span>
              </Button>
              {canInspectTokenValues && (
                <Button color="primary" component={Link} to={`${match.url}/token_values`}>
                  <BracketsCurlyIcon iconColor={colors.teal[700]} size={20} />
                  <span className="ml-8">Tokens Values</span>
                </Button>
              )}
            </div>
          </div>
          <CardDialog title="Templates">
            <InnerTabs tabs={activeTabs} />
          </CardDialog>
        </div>
      </Route>
    </Switch>
  );
};

export default GenericTemplatesConfigurationScreen;
